import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Document | null} */
  #cachedProductHtml = null;
  /** @type {Promise<void> | null} */
  #fetchPromise = null;

  get cachedProductHtml() {
    return this.#cachedProductHtml;
  }

  get productPageUrl() {
    return /** @type {HTMLAnchorElement} */ (this.closest('product-card')?.querySelector('a[ref="productCardLink"]'))
      ?.href;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    this.addEventListener('pointerdown', this.prefetchProductPage, { once: true, passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!this.#cachedProductHtml) {
      this.#showLoadingState();
      this.#openQuickAddModal();
      try {
        await this.fetchProductPage(this.productPageUrl);
      } catch (error) {
        this.#closeQuickAddModal();
        return;
      }
    }

    if (this.#cachedProductHtml) {
      // Create a fresh copy of the cached HTML to avoid modifying the original
      const freshHtmlCopy = new DOMParser().parseFromString(
        this.#cachedProductHtml.documentElement.outerHTML,
        'text/html'
      );

      const didUpdate = await this.updateQuickAddModal(freshHtmlCopy);
      if (!didUpdate) {
        this.#closeQuickAddModal();
        return;
      }
    }

    this.#openQuickAddModal();
  };

  prefetchProductPage = () => {
    this.fetchProductPage(this.productPageUrl)?.catch(() => {});
  };

  #showLoadingState() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const loading = document.createElement('div');
    loading.className = 'quick-add-modal__loading';
    loading.setAttribute('role', 'status');
    loading.textContent = 'Loading product…';
    modalContent.replaceChildren(loading);
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<void>}
   * @throws {Error} If the fetch request fails or returns a non-200 response
   */
  fetchProductPage(productPageUrl) {
    if (!productPageUrl) return;
    if (this.#cachedProductHtml) return Promise.resolve();
    if (this.#fetchPromise) return this.#fetchPromise;

    this.#fetchPromise = this.#fetchProductPage(productPageUrl).finally(() => {
      this.#fetchPromise = null;
    });
    return this.#fetchPromise;
  }

  async #fetchProductPage(productPageUrl) {

    // Every caller shares the same in-flight request through fetchProductPage,
    // so hovering a card and then tapping Add cannot cancel and restart its load.
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      // Store the HTML for later use
      this.#cachedProductHtml = html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   */
  async updateQuickAddModal(newHtml) {
    const productGrid = newHtml.querySelector('[data-product-grid-content]');
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return false;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      if (!productDetails) return false;
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const comboVariantPicker = productGrid.querySelector('combo-variant-picker');
      const comboBuyButton = productGrid.querySelector('combo-buy-button');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const hasStandardQuickAdd = productFormComponent && variantPicker;
      const hasComboQuickAdd = comboVariantPicker && comboBuyButton;
      if (!productPrice || (!hasStandardQuickAdd && !hasComboQuickAdd)) return false;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      productHeader.appendChild(productPrice);
      productGrid.appendChild(productHeader);
      if (hasComboQuickAdd) {
        productGrid.appendChild(comboVariantPicker);
        productGrid.appendChild(comboBuyButton);
      } else {
        productGrid.appendChild(variantPicker);
        productGrid.appendChild(productFormComponent);
      }
      productDetails.remove();
    }

    morph(modalContent, productGrid);
    return true;
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}





// import { morph } from '@theme/morph';
// import { Component } from '@theme/component';
// import { CartUpdateEvent, ThemeEvents } from '@theme/events';
// import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
// import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

// export class QuickAddComponent extends Component {
//   /** @type {AbortController | null} */
//   #abortController = null;
//   /** @type {Document | null} */
//   #cachedProductHtml = null;

//   get cachedProductHtml() {
//     return this.#cachedProductHtml;
//   }

//   get productPageUrl() {
//     return /** @type {HTMLAnchorElement} */ (this.closest('product-card')?.querySelector('a[ref="productCardLink"]'))
//       ?.href;
//   }

//   connectedCallback() {
//     super.connectedCallback();

//     mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
//   }

//   disconnectedCallback() {
//     super.disconnectedCallback();

//     mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
//   }

//   /**
//    * Handles quick add button click
//    * @param {Event} event - The click event
//    */
//   handleClick = async (event) => {
//     event.preventDefault();

//     if (!this.#cachedProductHtml) {
//       await this.fetchProductPage(this.productPageUrl);
//     }

//     if (this.#cachedProductHtml) {
//       // Create a fresh copy of the cached HTML to avoid modifying the original
//       const freshHtmlCopy = new DOMParser().parseFromString(
//         this.#cachedProductHtml.documentElement.outerHTML,
//         'text/html'
//       );

//       await this.updateQuickAddModal(freshHtmlCopy);
//     }

//     this.#openQuickAddModal();
//   };

//   /** @param {QuickAddDialog} dialogComponent */
//   #stayVisibleUntilDialogCloses(dialogComponent) {
//     this.toggleAttribute('stay-visible', true);

//     dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
//       once: true,
//     });
//   }

//   #openQuickAddModal = () => {
//     const dialogComponent = document.getElementById('quick-add-dialog');
//     if (!(dialogComponent instanceof QuickAddDialog)) return;

//     this.#stayVisibleUntilDialogCloses(dialogComponent);

//     dialogComponent.showDialog();
//   };

//   #closeQuickAddModal = () => {
//     const dialogComponent = document.getElementById('quick-add-dialog');
//     if (!(dialogComponent instanceof QuickAddDialog)) return;

//     dialogComponent.closeDialog();
//   };

//   /**
//    * Fetches the product page content
//    * @param {string} productPageUrl - The URL of the product page to fetch
//    * @returns {Promise<void>}
//    * @throws {Error} If the fetch request fails or returns a non-200 response
//    */
//   async fetchProductPage(productPageUrl) {
//     if (!productPageUrl) return;

//     // We use this to abort the previous fetch request if it's still pending.
//     this.#abortController?.abort();
//     this.#abortController = new AbortController();

//     try {
//       const response = await fetch(productPageUrl, {
//         signal: this.#abortController.signal,
//       });

//       if (!response.ok) {
//         throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
//       }

//       const responseText = await response.text();
//       const html = new DOMParser().parseFromString(responseText, 'text/html');

//       // Store the HTML for later use
//       this.#cachedProductHtml = html;
//     } catch (error) {
//       if (error.name === 'AbortError') {
//         return;
//       } else {
//         throw error;
//       }
//     } finally {
//       this.#abortController = null;
//     }
//   }

//   /**
//    * Re-renders the variant picker.
//    * @param {Document} newHtml - The new HTML.
//    */
//   async updateQuickAddModal(newHtml) {
//     const productGrid = newHtml.querySelector('[data-product-grid-content]');
//     const modalContent = document.getElementById('quick-add-modal-content');

//     if (!productGrid || !modalContent) return;

//     if (isMobileBreakpoint()) {
//       const productDetails = productGrid.querySelector('.product-details');
//       if (!productDetails) return;
//       const productFormComponent = productGrid.querySelector('product-form-component');
//       const variantPicker = productGrid.querySelector('variant-picker');
//       const productPrice = productGrid.querySelector('product-price');
//       const productTitle = document.createElement('a');
//       productTitle.textContent = this.dataset.productTitle || '';

//       // Make product title as a link to the product page
//       productTitle.href = this.productPageUrl;

//       if (!productFormComponent || !variantPicker || !productPrice || !productTitle) return;

//       const productHeader = document.createElement('div');
//       productHeader.classList.add('product-header');

//       productHeader.appendChild(productTitle);
//       productHeader.appendChild(productPrice);
//       productGrid.appendChild(productHeader);
//       productGrid.appendChild(variantPicker);
//       productGrid.appendChild(productFormComponent);
//       productDetails.remove();
//     }

//     morph(modalContent, productGrid);
//   }
// }

// if (!customElements.get('quick-add-component')) {
//   customElements.define('quick-add-component', QuickAddComponent);
// }

// class QuickAddDialog extends DialogComponent {
//   #abortController = new AbortController();

//   connectedCallback() {
//     super.connectedCallback();

//     this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
//     this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

//     this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
//   }

//   disconnectedCallback() {
//     super.disconnectedCallback();

//     this.#abortController.abort();
//     this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
//   }

//   /**
//    * Closes the dialog
//    * @param {CartUpdateEvent} event - The cart update event
//    */
//   handleCartUpdate = (event) => {
//     if (event.detail.data.didError) return;
//     this.closeDialog();
//   };

//   #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
//     const anchorElement = /** @type {HTMLAnchorElement} */ (
//       event.detail.data.html?.querySelector('.view-product-title a')
//     );
//     const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
//     const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

//     if (!anchorElement) return;

//     if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
//     if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
//   };

//   #handleDialogClose = () => {
//     const iosVersion = getIOSVersion();
//     /**
//      * This is a patch to solve an issue with the UI freezing when the dialog is closed.
//      * To reproduce it, use iOS 16.0.
//      */
//     if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

//     requestAnimationFrame(() => {
//       /** @type {HTMLElement | null} */
//       const grid = document.querySelector('#ResultsList [product-grid-view]');
//       if (grid) {
//         const currentWidth = grid.getBoundingClientRect().width;
//         grid.style.width = `${currentWidth - 1}px`;
//         requestAnimationFrame(() => {
//           grid.style.width = '';
//         });
//       }
//     });
//   };
// }

// if (!customElements.get('quick-add-dialog')) {
//   customElements.define('quick-add-dialog', QuickAddDialog);
// }
